import { useContext } from 'react';

import { getI18nData, isBrowser } from '@lucid/utils';
import { graphql, useStaticQuery } from 'gatsby';
import { LayoutContext } from '@lucid/components';

import type { Lucid } from 'types/types';

export const placeholderButtons: Lucid.Directus.Generated.Menu_Link[] = [
  {
    id: 'placeholder',
    final_url: '#placeholder',
    default_link_text: 'Contact sales',
    display_as_button: true,
  },
];

export const placeholderLinks: Lucid.Directus.Generated.Menu_Link[] = [
  {
    id: 'placeholder-link',
    final_url: '#placeholder-link',
    default_link_text: 'Log in',
    display_as_button: true,
  },
];

type UseNavButtons = {
  directus: {
    contact: Lucid.Directus.Generated.Contact_Form;
    try: Lucid.Directus.Generated.Try_Now;
  };
  i18nData: Lucid.I18n.DataEdges;
};

// TODO: update to match all other useNavButton hooks
const useNavButtons = (
  username?: string,
  shouldHideSignUpButton = false,
): {
  navButtons: Lucid.Directus.Generated.Menu_Link[];
} => {
  const { shortCode } = useContext(LayoutContext);
  const { directus, i18nData } = useStaticQuery<UseNavButtons>(graphql`
    query useNavButtonsQuery {
      directus {
        contact: contact_form_by_id(id: 7) {
          translations {
            languages_code {
              short_code
            }
            path {
              url
            }
          }
        }
        try: try_now_by_id(id: 1) {
          translations {
            languages_code {
              short_code
            }
            path {
              url
            }
          }
        }
      }
      i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
        edges {
          node {
            name
            childSrcJson {
              mktg_nav_try_now
              mktg_nav_sign_up_free
              mktg_nav_contact_sales
            }
          }
        }
      }
    }
  `);

  if (!isBrowser()) {
    return {
      navButtons: [],
    };
  }

  const i18n = getI18nData(i18nData, shortCode);
  const tryNowLinks = directus.try?.translations;
  const contactSalesLinks = directus.contact?.translations;
  const tryNowTranslatedEntity = tryNowLinks?.find(({ languages_code }) => languages_code?.short_code === shortCode);
  const contactSalesTranslatedEntity = contactSalesLinks?.find(
    ({ languages_code }) => languages_code?.short_code === shortCode,
  );
  const tryNowTranslatedPath = tryNowTranslatedEntity?.path?.url;
  const contactSalesTranslatedPath = contactSalesTranslatedEntity?.path?.url || '/contact/contact-sales';

  const loggedInButtons: Lucid.Directus.Generated.Menu_Link[] = [
    {
      id: 'try-now',
      final_url: tryNowTranslatedPath || '/try-now',
      default_link_text: i18n('mktg-nav-try-now'),
      beacon_id: 'try-now',
    },
    {
      id: 'contact-sales',
      final_url: contactSalesTranslatedPath || '/contact/contact-sales',
      default_link_text: i18n('mktg-nav-contact-sales'),
      beacon_id: 'contact-sales',
      display_as_button: true,
    },
  ];

  const initialLoggedOutButtons: Lucid.Directus.Generated.Menu_Link[] = [
    {
      id: 'try-now',
      final_url: tryNowTranslatedPath || '/try-now',
      default_link_text: i18n('mktg-nav-try-now'),
      beacon_id: 'try-now',
    },
    {
      id: 'contact-sales',
      final_url: contactSalesTranslatedPath || '/contact/contact-sales',
      default_link_text: i18n('mktg-nav-contact-sales'),
      beacon_id: 'contact-sales',
      display_as_button: true,
    },
  ];

  let loggedOutButtons = initialLoggedOutButtons;

  if (shouldHideSignUpButton) {
    loggedOutButtons = initialLoggedOutButtons.slice(0, 2);
  }

  return username
    ? {
        navButtons: loggedInButtons,
      }
    : {
        navButtons: loggedOutButtons,
      };
};

export default useNavButtons;
